import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ContactSelectionMode } from '../../contact';
import { StockSelectionMode } from '../../stock';
import { ProductSelectionMode, type ProductSelectionContext } from '../../product';
import type { Contact } from '../../../types/contact';
import type { Product } from '../../../types/product';
import type { Stock } from '../../../types/stock';

import type { ActivityFormData } from './types';

type SelectableFormField = 'client' | 'product' | 'provider' | 'stock' | 'stockFrom';

interface UseActivityNavigationParams {
    companyId: number;
    setActivities: React.Dispatch<React.SetStateAction<ActivityFormData[]>>;
}

function scrollToField(activityFeId: string, field: SelectableFormField) {
    const selector = `[data-field-target="${activityFeId}:${field}"]`;

    const scrollNow = () => {
        document.querySelector(selector)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    // Balances and activity types are still loading in asynchronously at this point
    // (slower on mobile connections), which shifts the layout and can push the
    // target back out of view after the first scroll. Re-align a couple more times
    // while things settle, but back off the moment the user starts scrolling themselves.
    const retryTimeouts = [300, 900].map(delay => window.setTimeout(scrollNow, delay));

    const cancelRetries = () => {
        retryTimeouts.forEach(window.clearTimeout);
        window.removeEventListener('touchstart', cancelRetries);
        window.removeEventListener('wheel', cancelRetries);
    };
    window.addEventListener('touchstart', cancelRetries, { once: true, passive: true });
    window.addEventListener('wheel', cancelRetries, { once: true, passive: true });

    requestAnimationFrame(scrollNow);
}

export function useActivityNavigation({
    companyId,
    setActivities,
}: UseActivityNavigationParams) {
    const navigate = useNavigate();
    const location = useLocation();

    // Handle returns from selection pages (client, product, provider, stock)
    useEffect(() => {
        const selectedClient = location.state?.selectedClient as Contact | undefined;
        const selectedProduct = location.state?.selectedProduct as Product | undefined;
        const selectedProvider = location.state?.selectedProvider as Contact | undefined;
        const selectedStock = location.state?.selectedStock as Stock | undefined;
        const selectedStockFrom = location.state?.selectedStockFrom as Stock | undefined;
        const activityFeId = location.state?.activityFeId as string | undefined;

        if (selectedClient && activityFeId) {
            setActivities(prev =>
                prev.map(activity =>
                    activity.feId === activityFeId
                        ? { ...activity, clientId: selectedClient.id, selectedClient, clientOrganizationName: undefined }
                        : activity
                )
            );
            scrollToField(activityFeId, 'client');
            navigate(location.pathname, { replace: true, state: {} });
        }

        if (selectedProduct && activityFeId) {
            setActivities(prev =>
                prev.map(activity =>
                    activity.feId === activityFeId
                        ? { ...activity, productId: selectedProduct.id, selectedProduct, margin: undefined, unitCostBasis: undefined }
                        : activity
                )
            );
            scrollToField(activityFeId, 'product');
            navigate(location.pathname, { replace: true, state: {} });
        }

        if (selectedProvider && activityFeId) {
            setActivities(prev =>
                prev.map(activity =>
                    activity.feId === activityFeId
                        ? { ...activity, providerId: selectedProvider.id, selectedProvider, providerOrganizationName: undefined }
                        : activity
                )
            );
            scrollToField(activityFeId, 'provider');
            navigate(location.pathname, { replace: true, state: {} });
        }

        if (selectedStock && activityFeId) {
            setActivities(prev =>
                prev.map(activity =>
                    activity.feId === activityFeId
                        ? { ...activity, stockId: selectedStock.id, selectedStock }
                        : activity
                )
            );
            scrollToField(activityFeId, 'stock');
            navigate(location.pathname, { replace: true, state: {} });
        }

        if (selectedStockFrom && activityFeId) {
            setActivities(prev =>
                prev.map(activity =>
                    activity.feId === activityFeId
                        ? { ...activity, stockFromId: selectedStockFrom.id, selectedStockFrom }
                        : activity
                )
            );
            scrollToField(activityFeId, 'stockFrom');
            navigate(location.pathname, { replace: true, state: {} });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.state]);

    const handleNavigateToAvailability = (productId: number) => {
        navigate(`/companies/${companyId}/stocks?products=${productId}`, {
            state: { returnPath: location.pathname },
        });
    };

    const handleOpenClientSelector = (activityFeId: string, currentClientId?: number) => {
        navigate(`/companies/${companyId}/contacts`, {
            state: {
                mode: ContactSelectionMode.SINGLE_CLIENT_SELECTION,
                activityFeId,
                currentContactId: currentClientId,
                returnPath: location.pathname,
            },
        });
    };

    const handleOpenProductSelector = (activityFeId: string, currentProductId?: number) => {
        const selectionContext: ProductSelectionContext = {
            mode: ProductSelectionMode.SINGLE_PRODUCT_SELECTION,
            activityFeId,
            currentProductId,
            returnPath: location.pathname,
        };
        navigate(`/companies/${companyId}/products`, { state: selectionContext });
    };

    const handleOpenProviderSelector = (activityFeId: string, currentProviderId?: number) => {
        navigate(`/companies/${companyId}/contacts`, {
            state: {
                mode: ContactSelectionMode.SINGLE_PROVIDER_SELECTION,
                activityFeId,
                currentContactId: currentProviderId,
                returnPath: location.pathname,
            },
        });
    };

    const handleOpenStockSelector = (activityFeId: string, currentStockId?: number) => {
        navigate(`/companies/${companyId}/stocks`, {
            state: {
                mode: StockSelectionMode.SINGLE_STOCK_SELECTION,
                activityFeId,
                currentStockId,
                returnPath: location.pathname,
            },
        });
    };

    const handleOpenStockFromSelector = (activityFeId: string, currentStockId?: number) => {
        navigate(`/companies/${companyId}/stocks`, {
            state: {
                mode: StockSelectionMode.SINGLE_STOCK_SELECTION,
                activityFeId,
                currentStockId,
                returnPath: location.pathname,
                returnStateKey: 'selectedStockFrom',
            },
        });
    };

    return {
        actions: {
            handleNavigateToAvailability,
            handleOpenClientSelector,
            handleOpenProductSelector,
            handleOpenProviderSelector,
            handleOpenStockSelector,
            handleOpenStockFromSelector,
        },
    };
}
